import React, { useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Circle, Text, Image, Transformer, RegularPolygon, Star, Arrow, Line as KonvaLine, Shape, Group, Ellipse, Ring } from 'react-konva';
import useImage from 'use-image';
import { useEditor } from '../../contexts/EditorContext';
import { useAuth } from '../../contexts/AuthContext';

// Helper: draw a shape clip path in local coords (0,0)→(w,h)
const drawMaskPath = (ctx, maskShape, w, h) => {
  const cx = w / 2, cy = h / 2;
  ctx.beginPath();
  switch (maskShape) {
    case 'rect':
      ctx.rect(0, 0, w, h);
      break;
    case 'circle':
      ctx.arc(cx, cy, Math.min(w, h) / 2, 0, Math.PI * 2);
      break;
    case 'triangle': {
      const r = Math.min(w, h) / 2;
      for (let i = 0; i < 3; i++) {
        const a = (i * 2 * Math.PI / 3) - Math.PI / 2;
        i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
                : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      ctx.closePath();
      break;
    }
    case 'diamond':
      ctx.moveTo(cx, 0); ctx.lineTo(w, cy);
      ctx.lineTo(cx, h); ctx.lineTo(0, cy);
      ctx.closePath();
      break;
    case 'pentagon': case 'hexagon': case 'octagon': {
      const sides = { pentagon: 5, hexagon: 6, octagon: 8 }[maskShape];
      const rp = Math.min(w, h) / 2;
      for (let i = 0; i < sides; i++) {
        const a = (i * 2 * Math.PI / sides) - Math.PI / 2;
        i === 0 ? ctx.moveTo(cx + rp * Math.cos(a), cy + rp * Math.sin(a))
                : ctx.lineTo(cx + rp * Math.cos(a), cy + rp * Math.sin(a));
      }
      ctx.closePath();
      break;
    }
    case 'star': {
      const outerR = Math.min(w, h) / 2, innerR = outerR * 0.4;
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const a = (i * Math.PI / 5) - Math.PI / 2;
        i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
                : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      ctx.closePath();
      break;
    }
    case 'cross': {
      const arm = w * 0.28;
      ctx.moveTo(arm, 0);       ctx.lineTo(w - arm, 0);
      ctx.lineTo(w - arm, arm); ctx.lineTo(w, arm);
      ctx.lineTo(w, h - arm);   ctx.lineTo(w - arm, h - arm);
      ctx.lineTo(w - arm, h);   ctx.lineTo(arm, h);
      ctx.lineTo(arm, h - arm); ctx.lineTo(0, h - arm);
      ctx.lineTo(0, arm);       ctx.lineTo(arm, arm);
      ctx.closePath();
      break;
    }
    case 'heart':
      ctx.moveTo(cx, h * 0.4);
      ctx.bezierCurveTo(w * 0.48, h * 0.08, 0, h * 0.08, 0, h * 0.4);
      ctx.bezierCurveTo(0, h * 0.68, cx, h * 0.88, cx, h);
      ctx.bezierCurveTo(cx, h * 0.88, w, h * 0.68, w, h * 0.4);
      ctx.bezierCurveTo(w, h * 0.08, w * 0.52, h * 0.08, cx, h * 0.4);
      ctx.closePath();
      break;
    case 'cloud':
      ctx.arc(w * 0.22, h * 0.58, h * 0.32, Math.PI * 0.5,  Math.PI * 1.5,  false);
      ctx.arc(w * 0.45, h * 0.3,  h * 0.26, Math.PI * 1.1,  Math.PI * 1.95, false);
      ctx.arc(w * 0.7,  h * 0.25, h * 0.22, Math.PI * 1.6,  Math.PI * 1.98, false);
      ctx.arc(w * 0.8,  h * 0.55, h * 0.28, Math.PI * 1.55, Math.PI * 0.5,  false);
      ctx.closePath();
      break;
    case 'rounded-rect': {
      const r = Math.min(w, h) * 0.15;
      ctx.moveTo(r, 0);    ctx.lineTo(w - r, 0);
      ctx.arcTo(w, 0, w, r, r);   ctx.lineTo(w, h - r);
      ctx.arcTo(w, h, w - r, h, r); ctx.lineTo(r, h);
      ctx.arcTo(0, h, 0, h - r, r); ctx.lineTo(0, r);
      ctx.arcTo(0, 0, r, 0, r);   ctx.closePath();
      break;
    }
    case 'ellipse':
      ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
      break;
    case 'arrow-right': {
      const aw = w * 0.38, sy = h * 0.28;
      ctx.moveTo(0, sy);
      ctx.lineTo(w - aw, sy);  ctx.lineTo(w - aw, 0);
      ctx.lineTo(w, cy);       ctx.lineTo(w - aw, h);
      ctx.lineTo(w - aw, h - sy); ctx.lineTo(0, h - sy);
      ctx.closePath();
      break;
    }
    case 'star6': {
      const outerR = Math.min(w, h) / 2, innerR = outerR * 0.5;
      for (let i = 0; i < 12; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const a = (i * Math.PI / 6) - Math.PI / 2;
        i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
                : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      ctx.closePath();
      break;
    }
    case 'speech-bubble': {
      const r = Math.min(w, h) * 0.1, bh = h * 0.76;
      const tx = w * 0.12, tw = w * 0.14;
      ctx.moveTo(r, 0);        ctx.lineTo(w - r, 0);
      ctx.arcTo(w, 0, w, r, r);    ctx.lineTo(w, bh - r);
      ctx.arcTo(w, bh, w - r, bh, r); ctx.lineTo(tx + tw, bh);
      ctx.lineTo(tx, h);       ctx.lineTo(tx, bh);
      ctx.lineTo(r, bh);
      ctx.arcTo(0, bh, 0, bh - r, r); ctx.lineTo(0, r);
      ctx.arcTo(0, 0, r, 0, r);   ctx.closePath();
      break;
    }
    case 'trapezoid': {
      const inset = w * 0.15;
      ctx.moveTo(inset, 0); ctx.lineTo(w - inset, 0);
      ctx.lineTo(w, h);     ctx.lineTo(0, h);
      ctx.closePath();
      break;
    }
    case 'shield': {
      const r = w * 0.12;
      ctx.moveTo(r, 0);    ctx.lineTo(w - r, 0);
      ctx.arcTo(w, 0, w, r, r); ctx.lineTo(w, h * 0.52);
      ctx.bezierCurveTo(w, h * 0.72, cx, h * 0.88, cx, h);
      ctx.bezierCurveTo(cx, h * 0.88, 0, h * 0.72, 0, h * 0.52);
      ctx.lineTo(0, r);    ctx.arcTo(0, 0, r, 0, r);
      ctx.closePath();
      break;
    }
    case 'chevron': {
      const notch = w * 0.28;
      ctx.moveTo(0, 0);          ctx.lineTo(w - notch, 0);
      ctx.lineTo(w, cy);         ctx.lineTo(w - notch, h);
      ctx.lineTo(0, h);          ctx.lineTo(notch, cy);
      ctx.closePath();
      break;
    }
    default:
      ctx.rect(0, 0, w, h);
  }
};

// Helper: build shadow props if shadow is enabled on element
const getShadowProps = (element) => element.shadowEnabled ? {
  shadowColor: element.shadowColor || '#000000',
  shadowBlur: element.shadowBlur ?? 10,
  shadowOffsetX: element.shadowOffsetX ?? 5,
  shadowOffsetY: element.shadowOffsetY ?? 5,
  shadowOpacity: element.shadowOpacity ?? 0.5,
} : {};

// Separate component for Konva Image to use useImage hook
const KonvaImage = ({ element, onClick, onDragEnd, onTransformEnd }) => {
  const [image] = useImage(element.src, 'anonymous');
  const isDraggable = !element.isBackground && element.draggable !== false;

  const interactiveProps = {
    id: element.id,
    draggable: isDraggable,
    listening: true,
    onClick: onClick,
    onDragEnd: isDraggable ? onDragEnd : undefined,
    onTransformEnd: isDraggable ? onTransformEnd : undefined,
    ...getShadowProps(element),
  };

  if (element.maskShape) {
    return (
      <Group
        {...interactiveProps}
        x={element.x}
        y={element.y}
        rotation={element.rotation || 0}
        opacity={element.opacity ?? 1}
        clipFunc={(ctx) => drawMaskPath(ctx, element.maskShape, element.width, element.height)}
      >
        <Image image={image} x={0} y={0} width={element.width} height={element.height} />
      </Group>
    );
  }

  return (
    <Image
      {...interactiveProps}
      image={image}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      rotation={element.rotation || 0}
      opacity={element.opacity ?? 1}
    />
  );
};

const KonvaCanvas = () => {
  const {
    slides,
    activeSlide,
    selectedElements,
    setSelectedElements,
    dimensions,
    stageRef,
    updateElement,
    zoom,
  } = useEditor();
  const { isPaidUser } = useAuth();
  
  const transformerRef = useRef();
  const layerRef = useRef();
  
  const currentSlide = slides[activeSlide];
  
  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && layerRef.current) {
      const allElements = currentSlide?.elements || [];
      const selectedNodes = selectedElements
        .map(id => layerRef.current.findOne(`#${id}`))
        .filter(Boolean)
        .filter(node => {
          const el = allElements.find(e => e.id === node.id());
          return !el?.locked;
        });
      
      transformerRef.current.nodes(selectedNodes);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedElements, currentSlide?.elements]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleSelect = (id, e) => {
    const metaPressed = e.evt?.shiftKey || e.evt?.ctrlKey || e.evt?.metaKey;
    
    if (metaPressed) {
      // Multi-select
      if (selectedElements.includes(id)) {
        // Remove from selection
        setSelectedElements(selectedElements.filter(elId => elId !== id));
      } else {
        // Add to selection
        setSelectedElements([...selectedElements, id]);
      }
    } else {
      // Single select
      setSelectedElements([id]);
    }
  };
  
  const handleDeselect = () => {
    setSelectedElements([]);
  };
  
  const handleDragEnd = (id, e) => {
    updateElement(id, {
      x: e.target.x(),
      y: e.target.y(),
    });
  };
  
  const handleTransformEnd = (id, e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    // Find the element to check its type
    const element = currentSlide?.elements.find(el => el.id === id);
    
    // Reset scale
    node.scaleX(1);
    node.scaleY(1);
    
    // Handle different element types
    const radialTypes = ['circle', 'triangle', 'diamond', 'pentagon', 'hexagon', 'octagon', 'crescent'];
    if (radialTypes.includes(element?.type)) {
      updateElement(id, {
        x: node.x(),
        y: node.y(),
        radius: Math.max(5, (element.radius || 80) * Math.max(scaleX, scaleY)),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'star') {
      updateElement(id, {
        x: node.x(),
        y: node.y(),
        outerRadius: Math.max(5, (element.outerRadius || 80) * Math.max(scaleX, scaleY)),
        innerRadius: Math.max(2, (element.innerRadius || 32) * Math.max(scaleX, scaleY)),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'ellipse') {
      updateElement(id, {
        x: node.x(), y: node.y(),
        radiusX: Math.max(5, (element.radiusX || 100) * scaleX),
        radiusY: Math.max(5, (element.radiusY || 60)  * scaleY),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'ring') {
      updateElement(id, {
        x: node.x(), y: node.y(),
        outerRadius: Math.max(5, (element.outerRadius || 80) * Math.max(scaleX, scaleY)),
        innerRadius: Math.max(2, (element.innerRadius || 40) * Math.max(scaleX, scaleY)),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'wavy-line') {
      updateElement(id, {
        x: node.x(), y: node.y(),
        length: Math.max(10, (element.length || 200) * scaleX),
        strokeWidth: Math.max(1, (element.strokeWidth || 4) * scaleY),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'image' && element?.maskShape) {
      updateElement(id, {
        x: node.x(),
        y: node.y(),
        width:  Math.max(5, element.width  * scaleX),
        height: Math.max(5, element.height * scaleY),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'line-shape' || element?.type === 'arrow-shape') {
      updateElement(id, {
        x: node.x(),
        y: node.y(),
        length: Math.max(10, (element.length || 200) * scaleX),
        strokeWidth: Math.max(1, (element.strokeWidth || 4) * scaleY),
        rotation: node.rotation(),
      });
    } else if (element?.type === 'text') {
      // For text, update width and fontSize
      updateElement(id, {
        x: node.x(),
        y: node.y(),
        width: Math.max(20, node.width() * scaleX),
        fontSize: Math.max(8, element.fontSize * scaleY),
        rotation: node.rotation(),
      });
    } else {
      // For rectangles and other shapes
      updateElement(id, {
        x: node.x(),
        y: node.y(),
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(5, node.height() * scaleY),
        rotation: node.rotation(),
      });
    }
  };
  
  const handleTextDblClick = (element) => {
    // Create textarea for editing
    const textNode = layerRef.current.findOne(`#${element.id}`);
    const stage = stageRef.current;
    
    // Hide text node
    textNode.hide();
    
    // Create textarea
    const textPosition = textNode.absolutePosition();
    const stageBox = stage.container().getBoundingClientRect();
    
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);
    
    textarea.value = element.text;
    textarea.style.position = 'absolute';
    textarea.style.top = `${stageBox.top + textPosition.y}px`;
    textarea.style.left = `${stageBox.left + textPosition.x}px`;
    textarea.style.width = `${textNode.width()}px`;
    textarea.style.fontSize = `${element.fontSize}px`;
    textarea.style.fontFamily = element.fontFamily;
    textarea.style.border = '2px solid #3B82F6';
    textarea.style.padding = '4px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'white';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.lineHeight = '1.2';
    textarea.style.transformOrigin = 'left top';
    textarea.style.textAlign = element.align || 'left';
    textarea.style.color = element.fill;
    textarea.style.fontWeight = (element.fontStyle || '').includes('bold') ? 'bold' : 'normal';
    textarea.style.fontStyle = (element.fontStyle || '').includes('italic') ? 'italic' : 'normal';
    textarea.style.textDecoration = element.textDecoration || 'none';
    
    textarea.focus();
    textarea.select();
    
    const removeTextarea = () => {
      textarea.parentNode?.removeChild(textarea);
      textNode.show();
      layerRef.current.batchDraw();
    };
    
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        updateElement(element.id, { text: textarea.value });
        removeTextarea();
      }
      if (e.key === 'Escape') {
        removeTextarea();
      }
    });
    
    textarea.addEventListener('blur', () => {
      updateElement(element.id, { text: textarea.value });
      removeTextarea();
    });
  };
  
  const renderElement = (element) => {
    const isLocked = element.locked || false;
    const isDraggable = !isLocked && !element.isBackground && element.draggable !== false;
    const commonProps = {
      key: element.id,
      id: element.id,
      draggable: isDraggable,
      onClick: isLocked ? undefined : (e) => handleSelect(element.id, e),
      onDragEnd: isDraggable ? (e) => handleDragEnd(element.id, e) : undefined,
      onTransformEnd: isDraggable ? (e) => handleTransformEnd(element.id, e) : undefined,
    };
    
    switch (element.type) {
      case 'text':
        return (
          <Text
            {...commonProps}
            x={element.x}
            y={element.y}
            text={element.text}
            fontSize={element.fontSize}
            fontFamily={element.fontFamily}
            fontStyle={element.fontStyle || 'normal'}
            textDecoration={element.textDecoration || ''}
            align={element.align || 'left'}
            letterSpacing={element.letterSpacing ?? 0}
            lineHeight={element.lineHeight ?? 1}
            fill={element.fill}
            width={element.width}
            rotation={element.rotation || 0}
            opacity={element.opacity ?? 1}
            onDblClick={() => handleTextDblClick(element)}
            {...getShadowProps(element)}
          />
        );
      
      case 'rect':
        return (
          <Rect
            {...commonProps}
            x={element.x}
            y={element.y}
            width={element.width}
            height={element.height}
            fill={element.fill}
            stroke={element.stroke}
            strokeWidth={element.strokeWidth || 0}
            cornerRadius={element.cornerRadius || 0}
            rotation={element.rotation || 0}
            opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );
      
      case 'circle':
        return (
          <Circle
            {...commonProps}
            x={element.x}
            y={element.y}
            radius={element.radius}
            fill={element.fill}
            stroke={element.stroke}
            strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0}
            opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );
      
      case 'image':
        return (
          <KonvaImage
            key={element.id}
            element={element}
            onClick={(e) => handleSelect(element.id, e)}
            onDragEnd={(e) => handleDragEnd(element.id, e)}
            onTransformEnd={(e) => handleTransformEnd(element.id, e)}
          />
        );
      
      case 'line-shape':
        return (
          <KonvaLine
            {...commonProps}
            x={element.x}
            y={element.y}
            points={[0, 0, element.length || 200, 0]}
            stroke={element.stroke || '#475569'}
            strokeWidth={element.strokeWidth || 4}
            dash={element.dash || []}
            rotation={element.rotation || 0}
            opacity={element.opacity ?? 1}
          />
        );

      case 'arrow-shape':
        return (
          <Arrow
            {...commonProps}
            x={element.x}
            y={element.y}
            points={[0, 0, element.length || 200, 0]}
            pointerLength={element.pointerLength || 20}
            pointerWidth={element.pointerWidth || 16}
            pointerAtBeginning={element.pointerAtBeginning || false}
            stroke={element.stroke || '#475569'}
            strokeWidth={element.strokeWidth || 4}
            fill={element.fill || '#475569'}
            rotation={element.rotation || 0}
            opacity={element.opacity ?? 1}
          />
        );

      case 'triangle':
        return (
          <RegularPolygon
            {...commonProps}
            x={element.x} y={element.y}
            sides={3} radius={element.radius || 90}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );

      case 'diamond':
        return (
          <RegularPolygon
            {...commonProps}
            x={element.x} y={element.y}
            sides={4} radius={element.radius || 90}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={(element.rotation || 0) + 45} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );

      case 'pentagon':
        return (
          <RegularPolygon
            {...commonProps}
            x={element.x} y={element.y}
            sides={5} radius={element.radius || 90}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );

      case 'hexagon':
        return (
          <RegularPolygon
            {...commonProps}
            x={element.x} y={element.y}
            sides={6} radius={element.radius || 90}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );

      case 'octagon':
        return (
          <RegularPolygon
            {...commonProps}
            x={element.x} y={element.y}
            sides={8} radius={element.radius || 90}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );

      case 'star': {
        return (
          <Star
            {...commonProps}
            x={element.x} y={element.y}
            numPoints={element.numPoints || 5}
            innerRadius={element.innerRadius || 36}
            outerRadius={element.outerRadius || 90}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}
          />
        );
      }

      case 'cross': {
        const cw = element.width || 160;
        const ch = element.height || 160;
        const arm = cw * 0.28;
        return (
          <Shape
            {...commonProps}
            x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            width={cw} height={ch}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(arm, 0);
              ctx.lineTo(cw - arm, 0);
              ctx.lineTo(cw - arm, arm);
              ctx.lineTo(cw,       arm);
              ctx.lineTo(cw,       ch - arm);
              ctx.lineTo(cw - arm, ch - arm);
              ctx.lineTo(cw - arm, ch);
              ctx.lineTo(arm,      ch);
              ctx.lineTo(arm,      ch - arm);
              ctx.lineTo(0,        ch - arm);
              ctx.lineTo(0,        arm);
              ctx.lineTo(arm,      arm);
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'heart': {
        const hw = element.width || 160;
        const hh = element.height || 150;
        return (
          <Shape
            {...commonProps}
            x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            width={hw} height={hh}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              const w = hw, h = hh;
              ctx.beginPath();
              ctx.moveTo(w/2, h*0.4);
              ctx.bezierCurveTo(w*0.48, h*0.08, 0, h*0.08, 0, h*0.4);
              ctx.bezierCurveTo(0, h*0.68, w/2, h*0.88, w/2, h);
              ctx.bezierCurveTo(w, h*0.88, w, h*0.68, w, h*0.4);
              ctx.bezierCurveTo(w, h*0.08, w*0.52, h*0.08, w/2, h*0.4);
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'cloud': {
        const clw = element.width || 220;
        const clh = element.height || 140;
        return (
          <Shape
            {...commonProps}
            x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'}
            stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            width={clw} height={clh}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              const w = clw, h = clh;
              ctx.beginPath();
              ctx.arc(w*0.22, h*0.58, h*0.32, Math.PI*0.5,  Math.PI*1.5,  false);
              ctx.arc(w*0.45, h*0.3,  h*0.26, Math.PI*1.1,  Math.PI*1.95, false);
              ctx.arc(w*0.7,  h*0.25, h*0.22, Math.PI*1.6,  Math.PI*1.98, false);
              ctx.arc(w*0.8,  h*0.55, h*0.28, Math.PI*1.55, Math.PI*0.5,  false);
              ctx.closePath();
              ctx.fillStrokeShape(shape);
            }}
          />
        );
      }

      case 'ellipse':
        return (
          <Ellipse {...commonProps} x={element.x} y={element.y}
            radiusX={element.radiusX || 100} radiusY={element.radiusY || 60}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}/>
        );

      case 'ring':
        return (
          <Ring {...commonProps} x={element.x} y={element.y}
            innerRadius={element.innerRadius || 40} outerRadius={element.outerRadius || 80}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            {...getShadowProps(element)}/>
        );

      case 'wavy-line': {
        const wlen = element.length || 200, wamp = element.amplitude || 15, wfreq = element.frequency || 4;
        const wpts = [];
        for (let i = 0; i <= 60; i++) {
          wpts.push((i / 60) * wlen);
          wpts.push(Math.sin((i / 60) * Math.PI * 2 * wfreq) * wamp);
        }
        return (
          <KonvaLine {...commonProps} x={element.x} y={element.y}
            points={wpts} tension={0.4}
            stroke={element.stroke || '#475569'} strokeWidth={element.strokeWidth || 4}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}/>
        );
      }

      case 'curved-arrow': {
        const caw2 = element.width || 200, cah2 = element.height || 120;
        const col2 = element.stroke || '#475569', sw2 = element.strokeWidth || 4;
        const x1 = 0, y1 = cah2 * 0.72;
        const x2 = caw2, y2 = cah2 * 0.72;
        const cpx = caw2 / 2, cpy = 0;
        const angle = Math.atan2(y2 - cpy, x2 - cpx), as = sw2 * 4;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={col2} stroke={col2} strokeWidth={sw2}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={caw2} height={cah2}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(x1, y1);
              ctx.quadraticCurveTo(cpx, cpy, x2, y2);
              ctx.strokeShape(shape);
              ctx.beginPath();
              ctx.moveTo(x2, y2);
              ctx.lineTo(x2 - as * Math.cos(angle - 0.45), y2 - as * Math.sin(angle - 0.45));
              ctx.lineTo(x2 - as * Math.cos(angle + 0.45), y2 - as * Math.sin(angle + 0.45));
              ctx.closePath();
              ctx.fillShape(shape);
            }}/>
        );
      }

      case 'parallelogram': {
        const pw = element.width || 200, ph = element.height || 120, sk = pw * 0.2;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={pw} height={ph}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(sk, 0);      ctx.lineTo(pw, 0);
              ctx.lineTo(pw - sk, ph); ctx.lineTo(0, ph);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      case 'trapezoid': {
        const tw2 = element.width || 200, th2 = element.height || 120, ins = tw2 * 0.15;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={tw2} height={th2}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(ins, 0);        ctx.lineTo(tw2 - ins, 0);
              ctx.lineTo(tw2, th2);      ctx.lineTo(0, th2);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      case 'chevron': {
        const chw = element.width || 200, chh = element.height || 120, cn = chw * 0.28;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={chw} height={chh}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(0, 0);          ctx.lineTo(chw - cn, 0);
              ctx.lineTo(chw, chh/2);    ctx.lineTo(chw - cn, chh);
              ctx.lineTo(0, chh);        ctx.lineTo(cn, chh/2);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      case 'shield': {
        const shw = element.width || 160, shh = element.height || 180, sr = shw * 0.12;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={shw} height={shh}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(sr, 0); ctx.lineTo(shw - sr, 0);
              ctx.arcTo(shw, 0, shw, sr, sr);
              ctx.lineTo(shw, shh/2);
              ctx.bezierCurveTo(shw, shh*0.75, shw/2, shh*0.925, shw/2, shh);
              ctx.bezierCurveTo(shw/2, shh*0.925, 0, shh*0.75, 0, shh/2);
              ctx.lineTo(0, sr);
              ctx.arcTo(0, 0, sr, 0, sr);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      case 'callout': {
        const cltw = element.width || 200, clth = element.height || 160;
        const clr = Math.min(cltw, clth) * 0.1;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={cltw} height={clth}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              const bot = clth * 0.76;
              const tailx = cltw * 0.12, tailw = cltw * 0.14;
              ctx.beginPath();
              ctx.moveTo(clr, 0); ctx.lineTo(cltw - clr, 0);
              ctx.arcTo(cltw, 0, cltw, clr, clr);
              ctx.lineTo(cltw, bot - clr);
              ctx.arcTo(cltw, bot, cltw - clr, bot, clr);
              ctx.lineTo(tailx + tailw, bot);
              ctx.lineTo(tailx, clth); ctx.lineTo(tailx, bot);
              ctx.lineTo(clr, bot);
              ctx.arcTo(0, bot, 0, bot - clr, clr);
              ctx.lineTo(0, clr);
              ctx.arcTo(0, 0, clr, 0, clr);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      case 'crescent': {
        const cr2 = element.radius || 80;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1}
            width={cr2*2} height={cr2*2} offsetX={cr2} offsetY={cr2}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.arc(cr2*0.9, cr2, cr2,      -Math.PI*0.65, Math.PI*0.65, false);
              ctx.arc(cr2*1.2, cr2, cr2*0.85,  Math.PI*0.65, -Math.PI*0.65, true);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      case 'ribbon': {
        const rbw = element.width || 200, rbh = element.height || 80, rbn = rbh * 0.35;
        return (
          <Shape {...commonProps} x={element.x} y={element.y}
            fill={element.fill || '#94a3b8'} stroke={element.stroke || ''} strokeWidth={element.strokeWidth || 0}
            rotation={element.rotation || 0} opacity={element.opacity ?? 1} width={rbw} height={rbh}
            {...getShadowProps(element)}
            sceneFunc={(ctx, shape) => {
              ctx.beginPath();
              ctx.moveTo(0, 0);           ctx.lineTo(rbw, 0);
              ctx.lineTo(rbw - rbn, rbh/2); ctx.lineTo(rbw, rbh);
              ctx.lineTo(0, rbh);         ctx.lineTo(rbn, rbh/2);
              ctx.closePath(); ctx.fillStrokeShape(shape);
            }}/>
        );
      }

      default:
        return null;
    }
  };
  
  const zoomMarginH = ((zoom - 1) * dimensions.height) / 2;
  const zoomMarginW = ((zoom - 1) * dimensions.width) / 2;

  return (
    <div className="bg-gray-100 flex-1 overflow-auto">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        minHeight: `${dimensions.height * zoom + 40}px`,
        minWidth: `${dimensions.width * zoom + 40}px`,
      }}>
      <div
        className="shadow-2xl"
        style={{
          transform: `scale(${zoom})`,
          transformOrigin: 'center center',
          margin: `${zoomMarginH}px ${zoomMarginW}px`,
          userSelect: isPaidUser ? 'auto' : 'none',
          WebkitUserSelect: isPaidUser ? 'auto' : 'none',
          pointerEvents: 'auto',
        }}
      >
        <Stage
          ref={stageRef}
          width={dimensions.width}
          height={dimensions.height}
          onMouseDown={(e) => {
            // Deselect when clicking on empty area
            const clickedOnEmpty = e.target === e.target.getStage();
            if (clickedOnEmpty) {
              handleDeselect();
            }
          }}
          onTouchStart={(e) => {
            // Deselect when touching empty area
            const clickedOnEmpty = e.target === e.target.getStage();
            if (clickedOnEmpty) {
              handleDeselect();
            }
          }}
        >
          <Layer ref={layerRef}>
            {/* Background */}
            <Rect
              x={0}
              y={0}
              width={dimensions.width}
              height={dimensions.height}
              fill={currentSlide?.backgroundColor || '#FFFFFF'}
              listening={false}
            />
            
            {/* Elements */}
            {currentSlide?.elements.map(renderElement)}
            
            {/* Transformer */}
            <Transformer
              ref={transformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                // Limit resize
                if (newBox.width < 5 || newBox.height < 5) {
                  return oldBox;
                }
                return newBox;
              }}
            />
          </Layer>

          {/* Watermark — free users only, non-interactive, always on top */}
          {!isPaidUser && (
            <Layer listening={false}>
              <Text
                text="Created for free at NovialCraft.com"
                x={0}
                y={12}
                width={dimensions.width}
                align="center"
                fontSize={Math.max(11, Math.round(dimensions.width * 0.012))}
                fontFamily="Arial"
                fontStyle="bold"
                fill="rgba(255,255,255,0.80)"
                shadowColor="rgba(0,0,0,0.55)"
                shadowBlur={5}
                shadowOffsetX={1}
                shadowOffsetY={1}
                listening={false}
              />
            </Layer>
          )}
        </Stage>
      </div>
      </div>
    </div>
  );
};

export default KonvaCanvas;
